/**
 * Mouse and keyboard handling.
 *
 * Input never touches game state directly. It produces intents, and the game
 * shell turns those into commands that go through the lockstep pipeline, which
 * is what keeps a click on one machine and its effect on another in agreement.
 */

import type { GameRenderer } from '../render/renderer.js';

export interface DragBox {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

export interface InputIntents {
  /** Left-click drag finished: select units in this world-space box. */
  onSelect(box: DragBox, additive: boolean): void;
  /** Left click that was not a drag. */
  onClick(world: { x: number; y: number }, additive: boolean): void;
  /** Right click: move, attack, or harvest depending on what is under it. */
  onOrder(world: { x: number; y: number }, attackMove: boolean): void;
  /** Left click while a structure is waiting to be placed. */
  onPlace(tile: { x: number; y: number }): void;
  /** Escape or right click while placing. */
  onCancelPlacement(): void;
  onStop(): void;
  onControlGroup(index: number, set: boolean): void;
  onProduce(kind: number): void;
  onTogglePause(): void;
}

/** Screen-edge band, in pixels, that scrolls the camera. */
const EDGE_SCROLL_BAND = 24;
const EDGE_SCROLL_SPEED = 28; // tiles per second at default zoom
const KEY_SCROLL_SPEED = 34;
/** Pixels of movement before a click is treated as a drag. */
const DRAG_THRESHOLD = 5;

export class InputController {
  private keys = new Set<string>();
  private dragging = false;
  private dragStart = { x: 0, y: 0 };
  private dragCurrent = { x: 0, y: 0 };
  private pointerInside = false;
  private pointer = { x: 0, y: 0 };
  /** Set while the player is choosing a spot for a finished structure. */
  placementActive = false;

  constructor(
    private canvas: HTMLCanvasElement,
    private renderer: GameRenderer,
    private intents: InputIntents,
  ) {
    this.attach();
  }

  /** Current drag rectangle in CSS pixels, or null when not dragging. */
  get selectionBox(): DragBox | null {
    if (!this.dragging) return null;
    return {
      x0: this.dragStart.x,
      y0: this.dragStart.y,
      x1: this.dragCurrent.x,
      y1: this.dragCurrent.y,
    };
  }

  get pointerWorld(): { x: number; y: number } {
    return this.renderer.screenToWorld(this.pointer.x, this.pointer.y);
  }

  private attach(): void {
    const c = this.canvas;

    c.addEventListener('contextmenu', (e) => e.preventDefault());

    c.addEventListener('pointerdown', (e) => {
      c.setPointerCapture(e.pointerId);
      this.pointer = { x: e.clientX, y: e.clientY };

      if (e.button === 0) {
        if (this.placementActive) {
          const w = this.renderer.screenToWorld(e.clientX, e.clientY);
          this.intents.onPlace({ x: Math.floor(w.x), y: Math.floor(w.y) });
          return;
        }
        this.dragging = true;
        this.dragStart = { x: e.clientX, y: e.clientY };
        this.dragCurrent = { x: e.clientX, y: e.clientY };
      } else if (e.button === 2) {
        if (this.placementActive) {
          this.intents.onCancelPlacement();
          return;
        }
        const w = this.renderer.screenToWorld(e.clientX, e.clientY);
        this.intents.onOrder(w, this.keys.has('AltLeft') || this.keys.has('AltRight'));
      }
    });

    c.addEventListener('pointermove', (e) => {
      this.pointer = { x: e.clientX, y: e.clientY };
      this.pointerInside = true;
      if (this.dragging) this.dragCurrent = { x: e.clientX, y: e.clientY };
    });

    c.addEventListener('pointerleave', () => {
      this.pointerInside = false;
    });

    c.addEventListener('pointerup', (e) => {
      if (e.button !== 0 || !this.dragging) return;
      this.dragging = false;
      const additive = this.keys.has('ShiftLeft') || this.keys.has('ShiftRight');
      const dx = Math.abs(e.clientX - this.dragStart.x);
      const dy = Math.abs(e.clientY - this.dragStart.y);

      if (dx < DRAG_THRESHOLD && dy < DRAG_THRESHOLD) {
        this.intents.onClick(this.renderer.screenToWorld(e.clientX, e.clientY), additive);
        return;
      }
      const a = this.renderer.screenToWorld(this.dragStart.x, this.dragStart.y);
      const b = this.renderer.screenToWorld(e.clientX, e.clientY);
      this.intents.onSelect({ x0: a.x, y0: a.y, x1: b.x, y1: b.y }, additive);
    });

    c.addEventListener(
      'wheel',
      (e) => {
        e.preventDefault();
        const factor = e.deltaY > 0 ? 1.12 : 1 / 1.12;
        this.renderer.zoomBy(factor, { x: e.clientX, y: e.clientY });
      },
      { passive: false },
    );

    window.addEventListener('keydown', (e) => {
      // Let the browser keep its own shortcuts.
      if (e.ctrlKey && e.code !== 'KeyA') return;
      this.keys.add(e.code);

      if (e.code === 'Escape') {
        this.intents.onCancelPlacement();
        return;
      }
      if (e.code === 'KeyS') {
        this.intents.onStop();
        return;
      }
      if (e.code === 'Space') {
        e.preventDefault();
        this.intents.onTogglePause();
        return;
      }
      // Control groups: Ctrl+N assigns, N recalls.
      const digit = e.code.match(/^Digit([0-9])$/);
      if (digit) {
        this.intents.onControlGroup(Number(digit[1]), e.ctrlKey);
        e.preventDefault();
        return;
      }
      // Production hotkeys.
      const prod: Record<string, number> = {
        KeyQ: 0, // infantry
        KeyW: 1, // tank
        KeyE: 2, // harvester
        KeyR: 4, // refinery
        KeyT: 5, // barracks
        KeyY: 6, // war factory
      };
      if (e.code in prod && !e.ctrlKey) {
        this.intents.onProduce(prod[e.code]);
      }
    });

    window.addEventListener('keyup', (e) => this.keys.delete(e.code));
    window.addEventListener('blur', () => this.keys.clear());
  }

  /** Applies camera scrolling for this frame. */
  updateCamera(dt: number): void {
    const cam = this.renderer.cam;
    // Scroll speed scales with zoom so the view moves at a consistent visual
    // rate whether zoomed in on a fight or out over the whole map.
    const speed = (cam.zoom / 34) * dt;

    let dx = 0;
    let dy = 0;
    if (this.keys.has('ArrowLeft') || this.keys.has('KeyA')) dx -= KEY_SCROLL_SPEED;
    if (this.keys.has('ArrowRight') || this.keys.has('KeyD')) dx += KEY_SCROLL_SPEED;
    if (this.keys.has('ArrowUp')) dy -= KEY_SCROLL_SPEED;
    if (this.keys.has('ArrowDown')) dy += KEY_SCROLL_SPEED;

    if (this.pointerInside && !this.dragging) {
      const rect = this.canvas.getBoundingClientRect();
      const px = this.pointer.x - rect.left;
      const py = this.pointer.y - rect.top;
      if (px < EDGE_SCROLL_BAND) dx -= EDGE_SCROLL_SPEED;
      if (px > rect.width - EDGE_SCROLL_BAND) dx += EDGE_SCROLL_SPEED;
      if (py < EDGE_SCROLL_BAND) dy -= EDGE_SCROLL_SPEED;
      if (py > rect.height - EDGE_SCROLL_BAND) dy += EDGE_SCROLL_SPEED;
    }

    if (dx !== 0 || dy !== 0) {
      cam.x += dx * speed;
      cam.y += dy * speed;
    }
    this.renderer.applyCamera();
  }

  isHeld(code: string): boolean {
    return this.keys.has(code);
  }

  centerOn(x: number, y: number): void {
    this.renderer.cam.x = x;
    this.renderer.cam.y = y;
    this.renderer.applyCamera();
  }
}
